import jsPDF from "jspdf";
import html2canvas from "html2canvas";

// ─────────────────────────────────────────────────────────────────────────────
// TenderTrackAI — Native PDF report builder
// Produces a real, text-based, selectable PDF with a black background and
// yellow headings. Uses jsPDF's native text rendering for the entire report
// so the file is small (~50–150 KB), fast, and saves reliably across browsers
// / preview iframes / mobile webviews.
//
// The "All Language Summary" block (Hindi, Marathi, Tamil, Bengali, etc.) is
// rendered through html2canvas → image, because jsPDF's core Helvetica font
// only supports Latin-1. The image fallback lets every Indian script render
// correctly without bundling 13 different Noto font files.
// ─────────────────────────────────────────────────────────────────────────────

const YELLOW = [255, 214, 10]; // #FFD60A
const BODY = [229, 231, 235]; // #E5E7EB
const MUTED = [156, 163, 175]; // #9CA3AF
const DIVIDER = [42, 42, 42]; // #2A2A2A
const BLACK = [0, 0, 0];

const PAGE_W = 595.28; // A4 width in pt
const PAGE_H = 841.89; // A4 height in pt
const MARGIN_X = 46;
const MARGIN_TOP = 56;
const MARGIN_BOTTOM = 60;
const CONTENT_W = PAGE_W - MARGIN_X * 2;

const setFill = (pdf, rgb) => pdf.setFillColor(rgb[0], rgb[1], rgb[2]);
const setText = (pdf, rgb) => pdf.setTextColor(rgb[0], rgb[1], rgb[2]);
const setDraw = (pdf, rgb) => pdf.setDrawColor(rgb[0], rgb[1], rgb[2]);

// jsPDF core Helvetica doesn't have the ₹ glyph; substitute "Rs." to avoid
// the wrong-glyph rendering that would otherwise appear.
const sanitizeLatin = (v) =>
    (v === null || v === undefined ? "" : String(v))
        .replace(/₹\s?/g, "Rs. ")
        .replace(/[\u2018\u2019]/g, "'")
        .replace(/[\u201C\u201D]/g, '"')
        .replace(/—/g, "-")
        .replace(/–/g, "-");

const safeStr = (v) => sanitizeLatin(v);

function paintBackground(pdf) {
    setFill(pdf, BLACK);
    pdf.rect(0, 0, PAGE_W, PAGE_H, "F");
}

function ensureSpace(pdf, cursor, needed) {
    if (cursor.y + needed > PAGE_H - MARGIN_BOTTOM) {
        pdf.addPage();
        paintBackground(pdf);
        cursor.y = MARGIN_TOP;
        cursor.page += 1;
    }
}

function drawHeader(pdf, cursor) {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(20);
    setText(pdf, YELLOW);
    pdf.text("TenderTrackAI", MARGIN_X, cursor.y);

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    setText(pdf, MUTED);
    pdf.text("AI TENDER ANALYSIS REPORT", MARGIN_X, cursor.y + 14);

    const dateStr = new Date().toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
    pdf.setFontSize(9);
    setText(pdf, MUTED);
    pdf.text(`Generated: ${dateStr}`, PAGE_W - MARGIN_X, cursor.y, { align: "right" });
    pdf.text("tendertrackai.com", PAGE_W - MARGIN_X, cursor.y + 12, { align: "right" });

    cursor.y += 26;

    setDraw(pdf, YELLOW);
    pdf.setLineWidth(1.2);
    pdf.line(MARGIN_X, cursor.y, PAGE_W - MARGIN_X, cursor.y);
    cursor.y += 18;
}

function drawSectionHeading(pdf, cursor, label) {
    ensureSpace(pdf, cursor, 28);
    cursor.y += 8;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    setText(pdf, YELLOW);
    pdf.text(safeStr(label).toUpperCase(), MARGIN_X, cursor.y);
    cursor.y += 6;
    setDraw(pdf, DIVIDER);
    pdf.setLineWidth(0.6);
    pdf.line(MARGIN_X, cursor.y, PAGE_W - MARGIN_X, cursor.y);
    cursor.y += 12;
}

function drawBodyText(pdf, cursor, text, opts = {}) {
    const fontSize = opts.fontSize ?? 10.5;
    const lineGap = opts.lineGap ?? 4;
    const color = opts.color ?? BODY;
    const font = opts.bold ? "bold" : "normal";

    pdf.setFont("helvetica", font);
    pdf.setFontSize(fontSize);
    setText(pdf, color);

    const wrapWidth = opts.width ?? CONTENT_W;
    const lines = pdf.splitTextToSize(safeStr(text), wrapWidth);
    const lineHeight = fontSize * 1.15 + lineGap;

    for (const line of lines) {
        ensureSpace(pdf, cursor, lineHeight);
        pdf.text(line, opts.x ?? MARGIN_X, cursor.y);
        cursor.y += lineHeight;
    }
}

function drawTitleBlock(pdf, cursor, a) {
    drawBodyText(pdf, cursor, `TENDER REF: ${safeStr(a.tender_no)}`, {
        fontSize: 8.5,
        color: MUTED,
        lineGap: 2,
    });
    drawBodyText(pdf, cursor, a.name, {
        fontSize: 16,
        color: YELLOW,
        bold: true,
        lineGap: 2,
    });
    drawBodyText(pdf, cursor, a.department, {
        fontSize: 10.5,
        color: BODY,
        lineGap: 6,
    });

    ensureSpace(pdf, cursor, 24);
    drawPill(pdf, cursor, MARGIN_X, safeStr(a.eligibility_status), YELLOW, YELLOW);
    const w1 = pillWidth(pdf, safeStr(a.eligibility_status));
    drawPill(
        pdf,
        cursor,
        MARGIN_X + w1 + 6,
        `${safeStr(a.risk_level)} RISK`,
        BODY,
        [85, 85, 85],
    );
    const w2 = pillWidth(pdf, `${safeStr(a.risk_level)} RISK`);
    drawPill(
        pdf,
        cursor,
        MARGIN_X + w1 + w2 + 12,
        `MATCH ${safeStr(a.match_score)}/100`,
        BODY,
        [85, 85, 85],
    );
    cursor.y += 22;
}

function pillWidth(pdf, label) {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8);
    return pdf.getTextWidth(label) + 16;
}

function drawPill(pdf, cursor, x, label, textColor, borderColor) {
    const h = 16;
    const w = pillWidth(pdf, label);
    setDraw(pdf, borderColor);
    pdf.setLineWidth(0.8);
    pdf.rect(x, cursor.y, w, h, "S");
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8);
    setText(pdf, textColor);
    pdf.text(label, x + w / 2, cursor.y + h / 2 + 3, { align: "center" });
}

function drawMetricsGrid(pdf, cursor, a) {
    const cells = [
        { label: "Tender Value", value: a.value, sub: "" },
        { label: "EMD Amount", value: a.emd, sub: a.emd_mode },
        { label: "Last Date", value: a.deadline_date, sub: a.deadline_time },
        { label: "Published", value: a.published_date, sub: "" },
    ];
    const cellW = CONTENT_W / 4;
    const cellH = 64;
    ensureSpace(pdf, cursor, cellH + 6);

    cells.forEach((c, i) => {
        const x = MARGIN_X + i * cellW;
        setDraw(pdf, DIVIDER);
        pdf.setLineWidth(0.6);
        pdf.rect(x, cursor.y, cellW, cellH, "S");

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(7.5);
        setText(pdf, YELLOW);
        pdf.text(c.label.toUpperCase(), x + 10, cursor.y + 14);

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(11);
        setText(pdf, BODY);
        const valueLines = pdf.splitTextToSize(safeStr(c.value), cellW - 20);
        pdf.text(valueLines[0] || "", x + 10, cursor.y + 32);

        if (c.sub) {
            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(8.5);
            setText(pdf, MUTED);
            const subLines = pdf.splitTextToSize(safeStr(c.sub), cellW - 20);
            pdf.text(subLines[0] || "", x + 10, cursor.y + 50);
        }
    });

    cursor.y += cellH + 10;
}

function drawDocuments(pdf, cursor, docs) {
    docs.forEach((d, i) => {
        const num = String(i + 1).padStart(2, "0");
        const lineHeight = 10.5 * 1.15 + 4;
        const wrapped = pdf.splitTextToSize(safeStr(d), CONTENT_W - 22);

        ensureSpace(pdf, cursor, wrapped.length * lineHeight + 2);

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(10.5);
        setText(pdf, YELLOW);
        pdf.text(num + ".", MARGIN_X, cursor.y);

        pdf.setFont("helvetica", "normal");
        setText(pdf, BODY);
        wrapped.forEach((line) => {
            pdf.text(line, MARGIN_X + 22, cursor.y);
            cursor.y += lineHeight;
        });
        cursor.y += 2;
    });
}

function drawRisks(pdf, cursor, risks) {
    risks.forEach((r) => {
        const title = safeStr(r.title);
        const level = safeStr(r.level);
        const desc = safeStr(r.desc);

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(10);
        const descLines = pdf.splitTextToSize(desc, CONTENT_W - 24);
        const blockH = 24 + descLines.length * 12 + 10;
        ensureSpace(pdf, cursor, blockH + 6);

        setDraw(pdf, DIVIDER);
        pdf.setLineWidth(0.6);
        pdf.rect(MARGIN_X, cursor.y, CONTENT_W, blockH, "S");
        setFill(pdf, YELLOW);
        pdf.rect(MARGIN_X, cursor.y, 3, blockH, "F");

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(11);
        setText(pdf, YELLOW);
        pdf.text(title, MARGIN_X + 14, cursor.y + 16);

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8.5);
        setText(pdf, MUTED);
        pdf.text(`${level} risk`, MARGIN_X + 14 + pdf.getTextWidth(title) + 8, cursor.y + 16);

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(10);
        setText(pdf, BODY);
        descLines.forEach((line, idx) => {
            pdf.text(line, MARGIN_X + 14, cursor.y + 32 + idx * 12);
        });

        cursor.y += blockH + 8;
    });
}

// Renders the language summary block as a black-background HTML element
// using html2canvas, so any script (Devanagari, Tamil, Bengali, Arabic, etc.)
// renders correctly. Returns a JPEG dataURL + the natural pixel size, or null
// if rendering fails (we fall back to native text in that case).
async function renderLanguageSummaryImage(label, summary) {
    const container = document.createElement("div");
    container.style.cssText = [
        "position:fixed",
        "left:-10000px",
        "top:0",
        "width:1000px",
        "background:#000000",
        "padding:24px 28px",
        "font-family:'Noto Sans','Noto Sans Devanagari','Noto Sans Tamil','Noto Sans Bengali','Noto Sans Gujarati','Noto Sans Telugu','Noto Sans Kannada','Noto Sans Malayalam','Noto Sans Gurmukhi','Noto Sans Oriya','Arial Unicode MS',Arial,sans-serif",
        "box-sizing:border-box",
        "color:#E5E7EB",
        "font-size:18px",
        "line-height:1.7",
    ].join(";");
    container.innerHTML = `
        <div style="color:#9CA3AF;font-size:13px;letter-spacing:1.2px;text-transform:uppercase;margin-bottom:6px;">Summary</div>
        <div style="color:#FFD60A;font-size:15px;font-weight:bold;margin-bottom:14px;">${escapeHtml(label || "All Language")}</div>
        <div style="color:#E5E7EB;font-size:18px;line-height:1.7;">${escapeHtml(summary || "")}</div>
    `;
    document.body.appendChild(container);
    try {
        const canvas = await html2canvas(container, {
            backgroundColor: "#000000",
            scale: 2,
            useCORS: true,
            logging: false,
        });
        return {
            data: canvas.toDataURL("image/jpeg", 0.9),
            widthPx: canvas.width,
            heightPx: canvas.height,
        };
    } catch (err) {
        console.warn("[reportPdf] Language summary image render failed:", err);
        return null;
    } finally {
        container.remove();
    }
}

function escapeHtml(s) {
    return String(s ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

function drawImageBlock(pdf, cursor, image) {
    if (!image) return;
    // Fit the image to the page content width, preserving aspect.
    const aspect = image.widthPx / image.heightPx;
    let drawW = CONTENT_W;
    let drawH = drawW / aspect;
    const maxH = PAGE_H - MARGIN_TOP - MARGIN_BOTTOM - 40;
    if (drawH > maxH) {
        drawH = maxH;
        drawW = drawH * aspect;
    }
    ensureSpace(pdf, cursor, drawH + 6);
    pdf.addImage(image.data, "JPEG", MARGIN_X, cursor.y, drawW, drawH);
    cursor.y += drawH + 10;
}

function drawFooter(pdf, fileName) {
    const total = pdf.internal.pages.length - 1;
    for (let i = 1; i <= total; i++) {
        pdf.setPage(i);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(7.5);
        setText(pdf, MUTED);
        pdf.text(
            `TenderTrackAI (c) ${new Date().getFullYear()} - Verify all figures against the official tender document.`,
            MARGIN_X,
            PAGE_H - 28,
        );
        pdf.text(`Page ${i} / ${total}`, PAGE_W - MARGIN_X, PAGE_H - 28, {
            align: "right",
        });
        if (fileName) {
            pdf.text(`Source: ${safeStr(fileName)}`, MARGIN_X, PAGE_H - 16);
        }
    }
}

function triggerDownload(blob, fileName) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.rel = "noopener";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
        a.remove();
        URL.revokeObjectURL(url);
    }, 200);
}

export async function downloadAnalysisPdf(a) {
    // Pre-render the multilingual summary as an image (so non-Latin scripts
    // render correctly). Done up-front to avoid blocking the layout.
    const summaryImg = await renderLanguageSummaryImage(
        a.language_summary_label,
        a.language_summary,
    );

    const pdf = new jsPDF({ unit: "pt", format: "a4", orientation: "portrait" });
    const cursor = { y: MARGIN_TOP, page: 1 };

    paintBackground(pdf);
    drawHeader(pdf, cursor);
    drawTitleBlock(pdf, cursor, a);

    drawSectionHeading(pdf, cursor, "Key Details");
    drawMetricsGrid(pdf, cursor, a);

    drawSectionHeading(pdf, cursor, "Executive Summary");
    drawBodyText(pdf, cursor, a.summary, { fontSize: 10.5, lineGap: 5 });

    drawSectionHeading(
        pdf,
        cursor,
        `Summary - ${safeStr(a.language_summary_label || "All Language")}`,
    );
    if (summaryImg) {
        drawImageBlock(pdf, cursor, summaryImg);
    } else {
        // Fallback: render natively (works for English, garbled for non-Latin).
        drawBodyText(pdf, cursor, a.language_summary, {
            fontSize: 10.5,
            lineGap: 5,
        });
    }

    const docs = a.documents || [];
    drawSectionHeading(pdf, cursor, `Required Documents (${docs.length})`);
    drawDocuments(pdf, cursor, docs);

    const risks = a.risks || [];
    drawSectionHeading(pdf, cursor, "Risk Factors");
    drawRisks(pdf, cursor, risks);

    drawSectionHeading(pdf, cursor, "Suggested Next Action");
    drawBodyText(pdf, cursor, a.next_action, { fontSize: 10.5, lineGap: 5 });

    drawFooter(pdf, a.file_name);

    const safeName = String(a.tender_no || a.name || "tender-analysis")
        .replace(/[^\w-]+/g, "_")
        .slice(0, 60);
    const dateTag = new Date().toISOString().slice(0, 10);
    const fileName = `TenderTrackAI_${safeName}_${dateTag}.pdf`;

    const blob = pdf.output("blob");
    triggerDownload(blob, fileName);
    return fileName;
}
